import React, { useState, useCallback, useEffect } from 'react';
import FileUpload from './FileUpload';
import KeywordTable, { Keyword } from './KeywordTable';
import { KeywordsProvider } from '../apps/KeywordsContext';
import Introduction from './Introduction';
import DownloadWord from './DownloadWord';
import DownloadExcel from './DownloadExcel';
import { initialKeywords, initialExcludedKeywords } from '../apps/KeywordsContext';
import useKeywords from '../hooks/useKeywords';

const MainContent: React.FC = () => {
    const { keywords, setKeywords, excludedKeywords, setExcludedKeywords } = useKeywords(
        'keywords',
        'excludedKeywords',
        initialKeywords,
        initialExcludedKeywords
    );
    const [selectedFile, setSelectedFile] = useState<FileList | null>(null);
    const [mergeStatus, setMergeStatus] = useState('idle');
    const [xmlContent, setXmlContent] = useState<string | null>(null);

    useEffect(() => {
        const storedKeywords = JSON.parse(localStorage.getItem('keywords') || '[]');
        const storedExcludedKeywords = JSON.parse(localStorage.getItem('excludedKeywords') || '[]');
        setKeywords(storedKeywords);
        setExcludedKeywords(storedExcludedKeywords);
    }, [setKeywords, setExcludedKeywords]);

    const handleMerge = useCallback((xmlDocResult: Document | null) => {
        if (xmlDocResult) {
            const xmlContent = new XMLSerializer().serializeToString(xmlDocResult);
            setXmlContent(xmlContent);
            setMergeStatus('completed');
        } else {
            setXmlContent(null);
            setMergeStatus('error');
        }
    }, []);

    const handleFileSelect = useCallback((files: FileList | null) => {
        setSelectedFile(files);
        setMergeStatus('idle');
    }, []);

    const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const droppedFile = e.dataTransfer.files;
        handleFileSelect(droppedFile);
    }, [handleFileSelect]);

    const handleKeywordsChange = useCallback((updatedKeywords: Keyword[]) => {
        setKeywords(updatedKeywords);
        // save localStorage
        localStorage.setItem('keywords', JSON.stringify(updatedKeywords));
        setMergeStatus('idle');
    }, [setKeywords]);

    const handleExcludedKeywordsChange = useCallback((updatedExcludedKeywords: Keyword[]) => {
        setExcludedKeywords(updatedExcludedKeywords);
        // save localStorage
        localStorage.setItem('excludedKeywords', JSON.stringify(updatedExcludedKeywords));
        setMergeStatus('idle');
    }, [setExcludedKeywords]);

    return (
        <div className='main-container' onDragOver={(e) => e.preventDefault()} onDrop={handleDrop}>
            <Introduction />
            <FileUpload onFileSelect={handleFileSelect} />
            <KeywordTable
                onKeywordsChange={handleKeywordsChange}
                onExcludedKeywordsChange={handleExcludedKeywordsChange}
                files={selectedFile}
                mergeStatus={mergeStatus}
                setMergeStatus={setMergeStatus}
                onMerge={handleMerge}
            />
            <DownloadWord
                uploadedFiles={selectedFile}
                keywords={keywords.map(k => k.keyword)}
                excludedKeywords={excludedKeywords.map(k => k.keyword)}
                mergeStatus={mergeStatus}
            />
            <DownloadExcel
                files={selectedFile}
                keywords={keywords}
                excludedKeywords={excludedKeywords}
                mergeStatus={mergeStatus}
                setMergeStatus={setMergeStatus}
                xmlContent={xmlContent}
            />
        </div>
    );
};

const Main: React.FC = () => {
    return (
        <KeywordsProvider>
            <MainContent />
        </KeywordsProvider>
    );
};

export default Main;
